import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { ArrowLeft, Fingerprint, KeyRound, Mail, ShieldCheck } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import {
  beginPasskeyLogin,
  beginPasskeyDeviceLinkRegistration,
  beginPasskeyRegistration,
  fetchInvitation,
  fetchPasskeyDeviceLink,
  finishPasskeyDeviceLinkRegistration,
  finishPasskeyLogin,
  finishPasskeyRegistration,
  requestEmailAccessCode,
  verifyEmailAccessCode,
} from "../lib/auth-api";
import { useI18n } from "../i18n";
import type { AuthSuccessResponse, InvitationInfo } from "../shared/auth";

export function AccessScreen({
  onAuthenticated,
}: {
  onAuthenticated: (session: AuthSuccessResponse) => void;
}) {
  const { text } = useI18n();
  const invitationToken = new URLSearchParams(window.location.search).get(
    "invite",
  );
  const deviceLinkToken = new URLSearchParams(window.location.search).get(
    "deviceInvite",
  );
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [credentialName, setCredentialName] = useState("Mi dispositivo");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [emailCodeMode, setEmailCodeMode] = useState<
    "passkey" | "request" | "verify"
  >("passkey");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState("");
  const supported = browserSupportsWebAuthn();

  useEffect(() => {
    setInvitation(null);
    setError("");
    if (!invitationToken && !deviceLinkToken) return;
    const loader = deviceLinkToken
      ? fetchPasskeyDeviceLink(deviceLinkToken)
      : fetchInvitation(invitationToken || "");
    loader
      .then(setInvitation)
      .catch((reason) =>
        setError(
          reason instanceof Error
            ? reason.message
            : text.access.linkError,
        ),
      );
  }, [deviceLinkToken, invitationToken, text.access.linkError]);

  async function login() {
    setPending(true);
    setError("");
    setNotice("");
    try {
      const generated = await beginPasskeyLogin();
      const response = await startAuthentication({
        optionsJSON: generated.options,
      });
      onAuthenticated(
        await finishPasskeyLogin({
          challengeId: generated.challengeId,
          response,
        }),
      );
    } catch (reason) {
      setError(formatPasskeyError(reason, text, "login"));
    } finally {
      setPending(false);
    }
  }

  async function register() {
    if (!invitationToken && !deviceLinkToken) return;
    setPending(true);
    setError("");
    setNotice("");
    try {
      const generated = deviceLinkToken
        ? await beginPasskeyDeviceLinkRegistration(deviceLinkToken)
        : await beginPasskeyRegistration(invitationToken || "");
      const response = await startRegistration({
        optionsJSON: generated.options,
      });
      const session = deviceLinkToken
        ? await finishPasskeyDeviceLinkRegistration({
            deviceLinkToken,
            challengeId: generated.challengeId,
            credentialName,
            response,
          })
        : await finishPasskeyRegistration({
            invitationToken: invitationToken || "",
            challengeId: generated.challengeId,
            credentialName,
            response,
          });
      onAuthenticated(session);
    } catch (reason) {
      setError(formatPasskeyError(reason, text, "registration"));
    } finally {
      setPending(false);
    }
  }

  async function requestCode(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    setNotice("");
    try {
      await requestEmailAccessCode({ email });
      setEmailCodeMode("verify");
      setNotice(text.access.emailCodeSent);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : text.access.emailCodeError);
    } finally {
      setPending(false);
    }
  }

  async function verifyCode(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    setNotice("");
    try {
      onAuthenticated(await verifyEmailAccessCode({ email, code }));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : text.access.emailCodeError);
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="access-shell">
      <section className="access-panel">
        <div className="access-brand">
          <div className="brand-mark">A</div>
          <div>
            <strong>Arenatours</strong>
            <span>Analytics 360</span>
          </div>
        </div>

        <div className="access-icon">
          {invitationToken || deviceLinkToken ? (
            <KeyRound size={27} />
          ) : (
            <Fingerprint size={29} />
          )}
        </div>

        {invitationToken || deviceLinkToken ? (
          <>
            <span className="eyebrow">
              {deviceLinkToken
                ? text.access.newDevice
                : text.access.secureInvitation}
            </span>
            <h1>
              {deviceLinkToken
                ? text.access.addPasskey
                : text.access.createPasskey}
            </h1>
            <p>
              {invitation
                ? `${invitation.displayName}, configura el acceso para ${invitation.email}.`
                : text.access.validatingLink}
            </p>
            <label className="access-field">
              <span>{text.access.deviceName}</span>
              <input
                value={credentialName}
                maxLength={80}
                onChange={(event) => setCredentialName(event.target.value)}
                placeholder={text.access.devicePlaceholder}
              />
            </label>
            <button
              className="access-primary"
              type="button"
              disabled={!invitation || !supported || pending}
              onClick={register}
            >
              <Fingerprint size={18} />
              {pending ? text.access.creatingPasskey : text.access.createAndEnter}
            </button>
          </>
        ) : (
          <>
            <span className="eyebrow">{text.access.privateAccess}</span>
            <h1>Analytics Dashboard</h1>
            {emailCodeMode === "passkey" ? (
              <>
                <p>{text.access.loginHelp}</p>
                <button
                  className="access-primary"
                  type="button"
                  disabled={!supported || pending}
                  onClick={login}
                >
                  <Fingerprint size={18} />
                  {pending ? text.access.verifying : text.access.passkeyLogin}
                </button>
                <button
                  className="access-secondary"
                  type="button"
                  disabled={pending}
                  onClick={() => {
                    setError("");
                    setNotice("");
                    setEmailCodeMode("request");
                  }}
                >
                  <Mail size={16} />
                  {text.access.emailCodeFallback}
                </button>
              </>
            ) : emailCodeMode === "request" ? (
              <form className="access-code-form" onSubmit={requestCode}>
                <p>{text.access.emailCodeHelp}</p>
                <label className="access-field">
                  <span>{text.access.email}</span>
                  <input
                    type="email"
                    value={email}
                    maxLength={254}
                    autoComplete="email"
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={text.access.emailPlaceholder}
                    required
                  />
                </label>
                <button
                  className="access-primary"
                  type="submit"
                  disabled={pending || !email}
                >
                  <Mail size={18} />
                  {pending ? text.access.sendingCode : text.access.sendCode}
                </button>
                <button
                  className="access-link-button"
                  type="button"
                  disabled={pending}
                  onClick={() => {
                    setError("");
                    setNotice("");
                    setEmailCodeMode("passkey");
                  }}
                >
                  <ArrowLeft size={14} />
                  {text.access.backToPasskey}
                </button>
              </form>
            ) : (
              <form className="access-code-form" onSubmit={verifyCode}>
                <p>{text.access.emailCodeVerifyHelp}</p>
                <label className="access-field">
                  <span>{text.access.code}</span>
                  <input
                    value={code}
                    maxLength={12}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    onChange={(event) => setCode(event.target.value)}
                    placeholder="000000"
                    required
                  />
                </label>
                <button
                  className="access-primary"
                  type="submit"
                  disabled={pending || code.replace(/\D/g, "").length !== 6}
                >
                  <KeyRound size={18} />
                  {pending ? text.access.verifyingCode : text.access.verifyCode}
                </button>
                <div className="access-code-actions">
                  <button
                    className="access-link-button"
                    type="button"
                    disabled={pending}
                    onClick={() => {
                      setError("");
                      setNotice("");
                      setCode("");
                      setEmailCodeMode("request");
                    }}
                  >
                    {text.access.requestAnotherCode}
                  </button>
                  <button
                    className="access-link-button"
                    type="button"
                    disabled={pending}
                    onClick={() => {
                      setError("");
                      setNotice("");
                      setCode("");
                      setEmailCodeMode("passkey");
                    }}
                  >
                    {text.access.backToPasskey}
                  </button>
                </div>
              </form>
            )}
          </>
        )}

        {!supported && emailCodeMode === "passkey" ? (
          <div className="access-error">
            {text.access.unsupported}
          </div>
        ) : null}
        {notice ? <div className="access-notice">{notice}</div> : null}
        {error ? <div className="access-error">{error}</div> : null}

        <div className="access-foot">
          <ShieldCheck size={15} />
          <span>{text.access.secureFoot}</span>
        </div>
      </section>
    </main>
  );
}

function formatPasskeyError(
  reason: unknown,
  text?: ReturnType<typeof useI18n>["text"],
  operation: "login" | "registration" = "login",
) {
  if (reason instanceof Error) {
    if (
      reason.name === "UnknownError" ||
      reason.message.includes(
        "The authenticator was unable to process the specified options",
      )
    ) {
      if (operation === "registration") {
        return `${text?.access.authenticatorRegistrationError || reason.message} (${reason.name})`;
      }
      return text?.access.authenticatorError || reason.message;
    }
    if (reason.name === "NotAllowedError") {
      return (
        text?.access.cancelled ||
        "La operación fue cancelada o superó el tiempo disponible."
      );
    }
    return reason.message;
  }
  return (
    text?.access.genericPasskeyError ||
    "No se pudo completar la operación con la Passkey."
  );
}
